import React from 'react';
import styled from 'styled-components';
import {Typography} from '../../elements/typography/Typography';
import {useBreakpointUp, useIsTwinmotionTheme} from 'epic-ue-theme';

export const absoluteCaptionOffset = 30;
export const captionWrapWidth = 32;
export interface WithCaptionProps {
    children?: React.ReactNode;
    text?: string;
    width?: number;
    forceBelow?: boolean;
    twoLineCaption?: boolean;
    tinyCaption?: boolean;
    forceDark?: boolean;
    adjustGradient?: boolean;
}

const NoCaption = styled.div`
    height: inherit;
`;

interface StyledCaptionProps {
    width?: number;
    captionWrapWidth?: number;
    absoluteCaptionOffset?: number;
}

const WithCaptionWrapper = styled.div<StyledCaptionProps>`
    height: inherit;
    width: ${(p) => (p.width ? `${p.width}px` : `100%`)};
    position: relative;
    overflow: visible;

    .overlay-caption-wrapper {
        text-align: right;
        display: block;
        position: absolute;
        bottom: 0;
        right: 0;
        padding: 0 ${(p) => p.theme.spacing(1)}px ${(p) => p.theme.spacing(0.5)}px 0;
        width: ${(p) => (p.width ? `${p.width}px` : `100%`)};
    }

    .bottom-caption-wrapper {
        text-align: right;
        display: block;
        height: auto;
        padding-top: ${(p) => p.theme.spacing(0.5)}px;
        ${(p) => p.theme.breakpoints.up('md')} {
            padding-top: ${(p) => p.theme.spacing(1)}px;
        }
        width: ${(p) => (p.width ? `${p.width}px` : `100%`)};
    }

    && .bottom-caption-wrapper {
        color: inherit;
    }
`;

const CaptionGradient = styled.div`
    && {
        height: 25%;
        width: 100%;
        position: absolute;
        bottom: 0;
        background: linear-gradient(0deg, rgb(14, 17, 40, 0.7), rgba(14, 17, 40, 0) 100%);
        .paragraph {
            color: ${(p) => p.theme.palette.neutral.stone};
            display: block;
            line-height: 16px;
            letter-spacing: 0.2px;
        }
        &.twinmotion {
            background: linear-gradient(0deg, rgb(27, 25, 30, 0.7), rgba(27, 25, 30, 0) 100%);
        }
    }
`;

export const WithCaption = ({
    children,
    text,
    forceBelow = false,
    width,
    twoLineCaption = false,
    tinyCaption = true
}: WithCaptionProps): JSX.Element => {
    const mdUp = useBreakpointUp('md');
    const isTwinmotion = useIsTwinmotionTheme();
    if (!text) {
        return <NoCaption className="with-caption ue-non-caption-wrap">{children}</NoCaption>;
    }

    let finalCaptionWrapWidth = captionWrapWidth;
    let finalAbsoluteCaptionOffset = absoluteCaptionOffset;
    if (twoLineCaption) {
        finalCaptionWrapWidth += 20;
        finalAbsoluteCaptionOffset += 24;
    }

    let overlayCaption = false;
    let belowCaption = true;
    let captionClassName = '';
    if (forceBelow) {
        captionClassName = 'bottom-caption-wrapper';
    } else {
        overlayCaption = mdUp;
        belowCaption = !mdUp;
        captionClassName = mdUp ? 'overlay-caption-wrapper' : 'bottom-caption-wrapper';
    }

    const caption = (
        <Typography
            className={captionClassName}
            variant="p"
            caption={!tinyCaption}
            tinyCaption={tinyCaption}
            accentColor="black">
            {text}
        </Typography>
    );

    return (
        <WithCaptionWrapper
            width={width}
            absoluteCaptionOffset={finalAbsoluteCaptionOffset}
            captionWrapWidth={finalCaptionWrapWidth}
            className={`with-caption`}>
            {children}
            {overlayCaption ? (
                <CaptionGradient className={`caption-gradient ${isTwinmotion ? 'twinmotion' : ''}`}>
                    {caption}
                </CaptionGradient>
            ) : (
                ''
            )}
            {belowCaption ? caption : ''}
        </WithCaptionWrapper>
    );
};
